import {
  NEW_LANGUAGE_PROMPT_TEXTS,
  type NewImpromptuLanguageCode,
} from "./new-language-prompt-library";

type ExtendedLanguageCode = "de" | "it" | "pt" | "ja" | "ko";
type ExtendedPromptCategory =
  | "opinion"
  | "story"
  | "explain"
  | "persuade"
  | "reflect"
  | "everyday"
  | "work"
  | "imagine";

type ExtendedPromptSeed = {
  slug: string;
  category: ExtendedPromptCategory;
  text: Record<ExtendedLanguageCode, string>;
};

/**
 * Fixed localizations of the same 32 reviewed prompt intents available in the
 * original four languages. Keeping these as authored content means selecting a
 * language never triggers generation and every category remains useful offline
 * from Gemini.
 */
const EXTENDED_PROMPT_SEEDS: ReadonlyArray<ExtendedPromptSeed> = [
  {
    slug: "meeting-free-day",
    category: "opinion",
    text: {
      de: "Sollte es jede Woche einen Tag ohne Besprechungen geben? Begründe deine Meinung.",
      it: "Dovrebbe esserci un giorno senza riunioni ogni settimana? Spiega la tua opinione.",
      pt: "Deveria haver um dia sem reuniões toda semana? Explique sua opinião.",
      ja: "毎週、会議をしない日を設けるべきですか。あなたの考えを説明してください。",
      ko: "매주 회의가 없는 날을 정해야 할까요? 자신의 생각을 설명해 보세요.",
    },
  },
  {
    slug: "city-centre-cars",
    category: "opinion",
    text: {
      de: "Sollte es in Innenstädten weniger Autos geben? Erkläre, wer davon profitieren würde und was schwieriger werden könnte.",
      it: "Dovrebbero esserci meno auto nei centri urbani? Spiega chi ne trarrebbe beneficio e cosa potrebbe diventare più difficile.",
      pt: "Deveria haver menos carros nos centros das cidades? Explique quem se beneficiaria e o que poderia ficar mais difícil.",
      ja: "都心部の車を減らすべきですか。誰にとって良いことなのか、また何が不便になるかを説明してください。",
      ko: "도심의 자동차 수를 줄여야 할까요? 누가 혜택을 받고 무엇이 더 불편해질 수 있는지 설명해 보세요.",
    },
  },
  {
    slug: "useful-mistake",
    category: "story",
    text: {
      de: "Erzähle von einem kleinen Fehler, aus dem du etwas Nützliches gelernt hast.",
      it: "Racconta la storia di un piccolo errore che ti ha insegnato qualcosa di utile.",
      pt: "Conte a história de um pequeno erro que ensinou algo útil a você.",
      ja: "小さな失敗から役に立つことを学んだ経験について話してください。",
      ko: "작은 실수를 통해 유용한 교훈을 얻은 경험을 이야기해 보세요.",
    },
  },
  {
    slug: "stranger-changed-day",
    category: "story",
    text: {
      de: "Erzähle von einem Moment, in dem eine fremde Person deinen Tag verändert hat.",
      it: "Descrivi una volta in cui una persona sconosciuta ha cambiato la tua giornata.",
      pt: "Descreva uma vez em que uma pessoa desconhecida mudou o seu dia.",
      ja: "知らない人との出来事で、その日が変わった経験について話してください。",
      ko: "낯선 사람이 당신의 하루를 바꾼 경험을 이야기해 보세요.",
    },
  },
  {
    slug: "culture",
    category: "explain",
    text: {
      de: "Erkläre einen Teil deiner Kultur, den Besucher oft falsch verstehen.",
      it: "Spiega un aspetto della tua cultura che i visitatori spesso fraintendono.",
      pt: "Explique um aspecto da sua cultura que os visitantes costumam entender mal.",
      ja: "訪れた人が誤解しやすい、あなたの文化の一面を説明してください。",
      ko: "방문객들이 자주 오해하는 당신의 문화 한 가지를 설명해 보세요.",
    },
  },
  {
    slug: "welcome-guest",
    category: "explain",
    text: {
      de: "Erkläre, wie man dafür sorgt, dass sich ein Gast zu Hause willkommen fühlt.",
      it: "Spiega come far sentire un ospite benvenuto a casa tua.",
      pt: "Explique como fazer um convidado se sentir bem-vindo em sua casa.",
      ja: "家に来たお客さんに居心地よく過ごしてもらう方法を説明してください。",
      ko: "집에 온 손님이 환영받는다고 느끼게 하는 방법을 설명해 보세요.",
    },
  },
  {
    slug: "activity",
    category: "persuade",
    text: {
      de: "Überzeuge einen Freund davon, eine Aktivität auszuprobieren, die dir Spaß macht.",
      it: "Convinci un amico a provare un'attività che ti piace.",
      pt: "Convença um amigo a experimentar uma atividade de que você gosta.",
      ja: "あなたが好きな活動を試してみるよう、友達を説得してください。",
      ko: "당신이 좋아하는 활동을 해 보도록 친구를 설득해 보세요.",
    },
  },
  {
    slug: "quiet-spaces",
    category: "persuade",
    text: {
      de: "Überzeuge deine Stadt davon, mehr ruhige öffentliche Orte zu schaffen.",
      it: "Convinci la tua città a creare più spazi pubblici tranquilli.",
      pt: "Convença sua cidade a criar mais espaços públicos tranquilos.",
      ja: "静かに過ごせる公共の場所を増やすよう、あなたの町を説得してください。",
      ko: "조용한 공공장소를 더 만들도록 당신이 사는 도시를 설득해 보세요.",
    },
  },
  {
    slug: "helpful-habit",
    category: "reflect",
    text: {
      de: "Welche Gewohnheit hat deinen Alltag verbessert, und warum funktioniert sie für dich?",
      it: "Quale abitudine ha migliorato la tua vita quotidiana e perché ha funzionato per te?",
      pt: "Qual hábito melhorou sua vida diária e por que ele funcionou para você?",
      ja: "毎日の生活を良くした習慣は何ですか。それが役に立った理由も説明してください。",
      ko: "일상을 더 나아지게 만든 습관은 무엇이며, 그 습관이 효과가 있었던 이유는 무엇인가요?",
    },
  },
  {
    slug: "small-decision",
    category: "reflect",
    text: {
      de: "Welche kleine Entscheidung hatte eine größere Wirkung, als du erwartet hattest?",
      it: "Quale piccola decisione ha avuto un effetto più grande di quanto ti aspettassi?",
      pt: "Qual pequena decisão teve um efeito maior do que você esperava?",
      ja: "予想していたより大きな影響を与えた、小さな決断について話してください。",
      ko: "예상보다 더 큰 영향을 준 작은 결정은 무엇이었나요?",
    },
  },
  {
    slug: "phones-at-meals",
    category: "opinion",
    text: {
      de: "Sollten Handys beim Essen mit anderen vom Tisch bleiben? Warum oder warum nicht?",
      it: "I telefoni dovrebbero restare lontani dal tavolo quando si mangia con altre persone? Perché?",
      pt: "Os celulares deveriam ficar fora da mesa durante refeições com outras pessoas? Por quê?",
      ja: "誰かと食事をするとき、携帯電話をテーブルに置かないほうがよいですか。理由も説明してください。",
      ko: "다른 사람들과 식사할 때 휴대전화를 식탁에서 치워야 할까요? 그 이유는 무엇인가요?",
    },
  },
  {
    slug: "cashless-life",
    category: "opinion",
    text: {
      de: "Ist eine Gesellschaft ohne Bargeld bequem oder riskant? Nenne das stärkste Argument für deine Meinung.",
      it: "Una società senza contanti è comoda o rischiosa? Presenta l'argomento più forte a favore della tua opinione.",
      pt: "Uma sociedade sem dinheiro em espécie é prática ou arriscada? Dê o melhor argumento para sua opinião.",
      ja: "キャッシュレス社会は便利ですか、それとも危険ですか。あなたの意見を最も強く支える理由を述べてください。",
      ko: "현금 없는 사회는 편리할까요, 아니면 위험할까요? 자신의 의견을 뒷받침할 가장 강한 근거를 말해 보세요.",
    },
  },
  {
    slug: "plan-changed",
    category: "story",
    text: {
      de: "Erzähle eine Geschichte über einen Plan, der sich in letzter Minute geändert hat.",
      it: "Racconta una storia su un piano che è cambiato all'ultimo minuto.",
      pt: "Conte uma história sobre um plano que mudou no último minuto.",
      ja: "直前に予定が変わったときのことを、物語として話してください。",
      ko: "마지막 순간에 계획이 바뀐 일을 이야기로 들려주세요.",
    },
  },
  {
    slug: "lost-object",
    category: "story",
    text: {
      de: "Erzähle die Geschichte von etwas, das du verloren hast, und wie die Situation endete.",
      it: "Racconta la storia di qualcosa che hai perso e di come si è conclusa la situazione.",
      pt: "Conte a história de algo que você perdeu e como a situação terminou.",
      ja: "何かをなくしたときのことと、その出来事がどう終わったかを話してください。",
      ko: "무언가를 잃어버린 일과 그 상황이 어떻게 끝났는지 이야기해 보세요.",
    },
  },
  {
    slug: "hobby-for-beginner",
    category: "explain",
    text: {
      de: "Erkläre, wie ein völliger Anfänger mit einem Hobby beginnen kann, das du gut kennst.",
      it: "Spiega come un principiante assoluto potrebbe iniziare un hobby che conosci bene.",
      pt: "Explique como um iniciante completo poderia começar um hobby que você conhece bem.",
      ja: "あなたがよく知っている趣味を、まったくの初心者が始める方法を説明してください。",
      ko: "당신이 잘 아는 취미를 완전한 초보자가 어떻게 시작하면 좋을지 설명해 보세요.",
    },
  },
  {
    slug: "your-neighbourhood",
    category: "explain",
    text: {
      de: "Beschreibe dein Viertel für jemanden, der darüber nachdenkt, dorthin zu ziehen.",
      it: "Descrivi il tuo quartiere a qualcuno che sta pensando di trasferirsi lì.",
      pt: "Descreva seu bairro para alguém que está pensando em se mudar para lá.",
      ja: "そこへの引っ越しを考えている人に、あなたの近所がどんな場所か説明してください。",
      ko: "그곳으로 이사를 고민하는 사람에게 당신의 동네를 설명해 보세요.",
    },
  },
  {
    slug: "choose-local-shop",
    category: "persuade",
    text: {
      de: "Überzeuge jemanden, lieber in einem lokalen Geschäft als bei einer großen Kette einzukaufen.",
      it: "Convinci qualcuno a scegliere un negozio locale invece di una grande catena.",
      pt: "Convença alguém a escolher uma loja local em vez de uma grande rede.",
      ja: "大きなチェーン店ではなく地元の店を選ぶよう、誰かを説得してください。",
      ko: "대형 체인점 대신 동네 가게를 선택하도록 누군가를 설득해 보세요.",
    },
  },
  {
    slug: "repair-not-replace",
    category: "persuade",
    text: {
      de: "Überzeuge Menschen davon, eine bestimmte Art von Gegenstand zu reparieren, statt sie zu ersetzen.",
      it: "Convinci le persone a riparare un tipo di oggetto invece di sostituirlo.",
      pt: "Convença as pessoas a consertar um tipo de objeto em vez de substituí-lo.",
      ja: "ある種類の物を買い替えずに修理するよう、人々を説得してください。",
      ko: "어떤 물건을 새것으로 바꾸는 대신 수리하도록 사람들을 설득해 보세요.",
    },
  },
  {
    slug: "advice-kept",
    category: "reflect",
    text: {
      de: "An welchen Rat denkst du noch heute, und warum ist er dir im Gedächtnis geblieben?",
      it: "A quale consiglio pensi ancora e perché ti è rimasto impresso?",
      pt: "Em qual conselho você ainda pensa e por que ele ficou na sua memória?",
      ja: "今でも覚えている助言は何ですか。なぜその言葉が心に残ったのかも説明してください。",
      ko: "아직도 생각나는 조언은 무엇이며, 왜 그 조언이 기억에 남았나요?",
    },
  },
  {
    slug: "meaningful-object",
    category: "reflect",
    text: {
      de: "Welcher gewöhnliche Gegenstand bedeutet dir mehr, als sein Preis vermuten lässt?",
      it: "Quale oggetto comune ha per te un valore maggiore di quanto suggerisca il suo prezzo?",
      pt: "Qual objeto comum significa mais para você do que o preço dele sugere?",
      ja: "値段以上の意味を持つ、身近な物は何ですか。その理由も話してください。",
      ko: "가격보다 더 큰 의미가 있는 평범한 물건은 무엇인가요?",
    },
  },
  {
    slug: "morning-routine",
    category: "everyday",
    text: {
      de: "Beschreibe deinen üblichen Morgen und nenne den Teil, den du am liebsten ändern würdest.",
      it: "Descrivi la tua mattina abituale, compresa la parte che vorresti cambiare di più.",
      pt: "Descreva sua manhã habitual, incluindo a parte que você mais gostaria de mudar.",
      ja: "普段の朝の過ごし方と、その中で最も変えたいことを説明してください。",
      ko: "평소 아침 일과와 그중 가장 바꾸고 싶은 부분을 설명해 보세요.",
    },
  },
  {
    slug: "comfort-meal",
    category: "everyday",
    text: {
      de: "Beschreibe ein Essen, das dir Trost oder ein vertrautes Gefühl gibt.",
      it: "Descrivi un pasto che scegli quando cerchi conforto o qualcosa di familiare.",
      pt: "Descreva uma refeição que você procura quando quer conforto ou algo familiar.",
      ja: "ほっとしたいときや懐かしさを感じたいときに食べる料理を説明してください。",
      ko: "위로나 익숙함이 필요할 때 찾게 되는 음식을 설명해 보세요.",
    },
  },
  {
    slug: "daily-journey",
    category: "everyday",
    text: {
      de: "Beschreibe einen Weg, den du oft zurücklegst, und ein Detail, das den meisten Menschen entgeht.",
      it: "Descrivi un tragitto che fai spesso e un dettaglio che la maggior parte delle persone non noterebbe.",
      pt: "Descreva um trajeto que você faz com frequência e um detalhe que a maioria das pessoas não perceberia.",
      ja: "よく通る道のりと、多くの人が気づかない細かな点を一つ説明してください。",
      ko: "자주 다니는 길과 대부분의 사람들이 놓칠 만한 세부 사항 하나를 설명해 보세요.",
    },
  },
  {
    slug: "favourite-room",
    category: "everyday",
    text: {
      de: "Beschreibe dein Lieblingszimmer oder deine Lieblingsecke und erkläre, warum du dich dort wohlfühlst.",
      it: "Descrivi la tua stanza o il tuo angolo preferito e spiega perché è adatto a te.",
      pt: "Descreva seu cômodo ou canto favorito e explique por que ele funciona bem para você.",
      ja: "お気に入りの部屋や場所を説明し、なぜそこで心地よく過ごせるのか話してください。",
      ko: "가장 좋아하는 방이나 공간을 설명하고, 그곳이 자신에게 잘 맞는 이유를 말해 보세요.",
    },
  },
  {
    slug: "receive-feedback",
    category: "work",
    text: {
      de: "Erkläre, wie du schwieriges Feedback am liebsten erhalten möchtest.",
      it: "Spiega come preferisci ricevere un feedback difficile.",
      pt: "Explique como você prefere receber um feedback difícil.",
      ja: "耳の痛い意見を、どのように伝えてもらいたいか説明してください。",
      ko: "받아들이기 어려운 피드백을 어떤 방식으로 받고 싶은지 설명해 보세요.",
    },
  },
  {
    slug: "competing-priorities",
    category: "work",
    text: {
      de: "Wie würdest du zwei wichtige Aufgaben mit derselben Frist bewältigen?",
      it: "Come gestiresti due compiti importanti con la stessa scadenza?",
      pt: "Como você lidaria com duas tarefas importantes com o mesmo prazo?",
      ja: "締め切りが同じ二つの重要な仕事に、どのように対応しますか。",
      ko: "마감일이 같은 두 가지 중요한 일을 어떻게 처리하겠어요?",
    },
  },
  {
    slug: "explain-your-role",
    category: "work",
    text: {
      de: "Erkläre deine Arbeit oder dein Studium jemandem, der nicht aus deinem Fachgebiet kommt.",
      it: "Spiega il tuo lavoro o i tuoi studi a qualcuno che non conosce il tuo settore.",
      pt: "Explique seu trabalho ou seus estudos para alguém de fora da sua área.",
      ja: "あなたの仕事や勉強について、その分野を知らない人に説明してください。",
      ko: "당신의 일이나 공부를 그 분야 밖에 있는 사람에게 설명해 보세요.",
    },
  },
  {
    slug: "missed-deadline",
    category: "work",
    text: {
      de: "Wie würdest du jemandem mitteilen, dass eine wichtige Frist nicht eingehalten werden kann?",
      it: "Come diresti a qualcuno che non sarà possibile rispettare una scadenza importante?",
      pt: "Como você diria a alguém que um prazo importante não será cumprido?",
      ja: "重要な締め切りに間に合わないことを、相手にどのように伝えますか。",
      ko: "중요한 마감일을 지키지 못하게 되었다는 사실을 어떻게 전달하겠어요?",
    },
  },
  {
    slug: "extra-hour",
    category: "imagine",
    text: {
      de: "Stell dir vor, jeder Tag hätte eine zusätzliche Stunde. Was würdest du damit tun, und was würde sich ändern?",
      it: "Immagina che ogni giorno abbia un'ora in più. Come la useresti e cosa cambierebbe?",
      pt: "Imagine que cada dia tivesse uma hora a mais. O que você faria com ela e o que mudaria?",
      ja: "毎日に一時間余分にあったら、何をしますか。また、生活はどのように変わりますか。",
      ko: "매일 한 시간이 더 생긴다고 상상해 보세요. 그 시간을 어떻게 쓰고 무엇이 달라질까요?",
    },
  },
  {
    slug: "exchange-lives",
    category: "imagine",
    text: {
      de: "Wenn du eine Woche lang mit jemandem den Alltag tauschen könntest, wen würdest du wählen und warum?",
      it: "Se potessi scambiare la tua vita quotidiana con qualcuno per una settimana, chi sceglieresti e perché?",
      pt: "Se você pudesse trocar de vida com alguém por uma semana, quem escolheria e por quê?",
      ja: "一週間だけ誰かと日常生活を交換できるなら、誰を選びますか。その理由も話してください。",
      ko: "일주일 동안 누군가와 일상을 바꿀 수 있다면 누구를 선택하고 그 이유는 무엇인가요?",
    },
  },
  {
    slug: "phone-free-weekend",
    category: "imagine",
    text: {
      de: "Stell dir ein Wochenende ohne Handys oder Internet vor. Was wäre leichter, und was wäre schwieriger?",
      it: "Immagina un fine settimana senza telefoni né internet. Cosa sarebbe più facile e cosa più difficile?",
      pt: "Imagine um fim de semana sem celulares nem internet. O que seria mais fácil e o que seria mais difícil?",
      ja: "携帯電話もインターネットもない週末を想像してください。何が楽になり、何が難しくなりますか。",
      ko: "휴대전화와 인터넷이 없는 주말을 상상해 보세요. 무엇이 더 쉬워지고 무엇이 더 어려워질까요?",
    },
  },
  {
    slug: "tiny-business",
    category: "imagine",
    text: {
      de: "Stell dir vor, du könntest morgen ein kleines Geschäft eröffnen. Was würde es anbieten, und wer würde kommen?",
      it: "Immagina di poter aprire domani una piccola attività. Cosa offrirebbe e chi la visiterebbe?",
      pt: "Imagine que você pudesse abrir um pequeno negócio amanhã. O que ele ofereceria e quem o visitaria?",
      ja: "明日、小さな店を開けるとしたら、何を提供し、どんな人が来ると思いますか。",
      ko: "내일 작은 사업을 시작할 수 있다고 상상해 보세요. 무엇을 제공하고 어떤 사람들이 찾아올까요?",
    },
  },
];

export function buildExtendedImpromptuPrompts(preparationSeconds: number, targetSeconds: number) {
  const existing = EXTENDED_PROMPT_SEEDS.flatMap((seed) =>
    (Object.keys(seed.text) as ExtendedLanguageCode[]).map((languageCode) => ({
      id: `${seed.category}-${seed.slug}-${languageCode}`,
      category: seed.category,
      languageCode,
      text: seed.text[languageCode],
      preparationSeconds,
      targetSeconds,
    }))
  );

  const additional = (
    Object.entries(NEW_LANGUAGE_PROMPT_TEXTS) as Array<
      [NewImpromptuLanguageCode, ReadonlyArray<string>]
    >
  ).flatMap(([languageCode, texts]) => {
    if (texts.length !== EXTENDED_PROMPT_SEEDS.length) {
      throw new Error(`Expected 32 impromptu prompts for ${languageCode}`);
    }
    return EXTENDED_PROMPT_SEEDS.map((seed, index) => ({
      id: `${seed.category}-${seed.slug}-${languageCode}`,
      category: seed.category,
      languageCode,
      text: texts[index]!,
      preparationSeconds,
      targetSeconds,
    }));
  });

  return [...existing, ...additional];
}
